import React, { createContext, useContext } from 'react';

const LaunchContext = createContext();

// Mock functions for standalone/development mode
const mockGetLaunchUrl = (data) => {
  console.group('🚀 [MockLaunchUrl] Simulating launch');
  console.log('📋 Launch data:', data);
  console.log('🔗 Activity ID:', data?.id || data?.activity_id || 'N/A');
  console.log('📚 Course ID:', data?.course_id || 'N/A');
  console.log('🛠️ Tool:', data?.tool || 'N/A');
  console.log('📑 Tab:', data?.tab || 'N/A');
  console.log('💡 In microfrontend mode, this would call the external getLaunchUrl function');
  console.groupEnd();
  
  const mockUrl = `#mock-launch-${data?.id || data?.activity_id || 'unknown'}`;
  return mockUrl;
};

const mockLaunchActivity = (assignment, courseShortId, productModelId, isCheckedReadingTabData) => {
  console.group('🚀 [MockLaunchActivity] Simulating activity launch');
  console.log('📋 Assignment:', assignment);
  console.log('📚 Course Short ID:', courseShortId);
  console.log('🆔 Product Model ID:', productModelId);
  console.log('📖 Is Checked Reading Tab Data:', isCheckedReadingTabData);
  console.log('💡 In microfrontend mode, this would call the external launchActivity function');
  console.groupEnd();

  return Promise.resolve({ status: 'mocked', message: 'Activity launch simulated' });
};

const defaultLaunchFunctions = {
  getLaunchUrl: mockGetLaunchUrl,
  launchActivity: mockLaunchActivity,
};

export const LaunchProvider = ({ children, getLaunchUrl, launchActivity }) => {
  const launchFunctions = {
    getLaunchUrl: getLaunchUrl || defaultLaunchFunctions.getLaunchUrl,
    launchActivity: launchActivity || defaultLaunchFunctions.launchActivity,
  };
  
  React.useEffect(() => {
    const mode = (getLaunchUrl || launchActivity) ? 'MICROFRONTEND' : 'STANDALONE';
    const externalFunctions = [];
    
    if (getLaunchUrl) externalFunctions.push('getLaunchUrl');
    if (launchActivity) externalFunctions.push('launchActivity');
    
    if (mode === 'MICROFRONTEND') {
      console.log(`🌐 [LaunchProvider] Running in MICROFRONTEND mode - using external: ${externalFunctions.join(', ')}`);
    } else {
      console.log('🏠 [LaunchProvider] Running in STANDALONE mode - using mock functions');
    }
  }, [getLaunchUrl, launchActivity]);
  
  return (
    <LaunchContext.Provider value={launchFunctions}>
      {children}
    </LaunchContext.Provider>
  );
};

export const useLaunchUrl = () => {
  const launchFunctions = useContext(LaunchContext);
  
  if (!launchFunctions) {
    throw new Error('useLaunchUrl must be used within a LaunchProvider');
  }
  
  return launchFunctions.getLaunchUrl;
};

export const useLaunchActivity = () => {
  const launchFunctions = useContext(LaunchContext);
  
  if (!launchFunctions) {
    throw new Error('useLaunchActivity must be used within a LaunchProvider');
  }
  
  return launchFunctions.launchActivity;
};

export const useLaunchMode = () => {
  const launchFunctions = useContext(LaunchContext);
  
  if (!launchFunctions) {
    throw new Error('useLaunchMode must be used within a LaunchProvider');
  }
  
  const isMicrofrontend = 
    launchFunctions.getLaunchUrl !== defaultLaunchFunctions.getLaunchUrl ||
    launchFunctions.launchActivity !== defaultLaunchFunctions.launchActivity;
  
  return {
    isMicrofrontend,
    isStandalone: !isMicrofrontend,
    mode: isMicrofrontend ? 'microfrontend' : 'standalone'
  };
};

export const LaunchUrlProvider = LaunchProvider;
export const useLaunchUrlMode = useLaunchMode;

export default LaunchContext;
